/**
 * In-memory sorting for fixed-size dashboard cards, which must reorder the rows they already hold. Sorting such a
 * card server-side would change which rows it contains: "top 5 by amount" is a different set to "top 5 by date".
 */

/** The comparable values a sortable column can yield. Dates arrive from the API as ISO strings. */
export type RowSortValue = string | number | null | undefined;

/** Resolves the value a row sorts by, for one column. */
export type RowSortValueAccessor<T> = (row: T) => RowSortValue;

/**
 * Returns a reordered copy, leaving the input untouched (it is typically a {@code shareReplay}ed page others hold).
 * Blanks sink to the bottom in either direction, so rows rendered as "-" can't displace real data. Ties keep their
 * incoming order.
 *
 * @param rows the loaded page; may be null or undefined
 * @param valueOf resolves the sort value for a row, or null when the column is not sortable
 * @param order requested direction
 * @return a new, reordered array — empty when there is nothing to sort
 */
export function sortLoadedRows<T>(
    rows: readonly T[] | null | undefined,
    valueOf: RowSortValueAccessor<T> | null | undefined,
    order: 'ASC' | 'DESC'): T[]
{
    if (!rows?.length)
    {
        return [];
    }
    if (!valueOf)
    {
        return [...rows];
    }

    const direction = order === 'ASC' ? 1 : -1;
    return [...rows].sort((left, right) =>
    {
        const leftValue = valueOf(left);
        const rightValue = valueOf(right);
        const leftBlank = isBlank(leftValue);
        const rightBlank = isBlank(rightValue);

        // Compared outside the direction multiplier, so flipping direction can't lift blanks above real values.
        if (leftBlank || rightBlank)
        {
            if (leftBlank && rightBlank)
            {
                return 0;
            }
            return leftBlank ? 1 : -1;
        }

        return compare(leftValue, rightValue) * direction;
    });
}

/**
 * Compares two non-blank values ascending: numerically for numbers, otherwise case-insensitive text collation.
 *
 * @param left first value
 * @param right second value
 * @return negative when left sorts first, positive when right does, 0 when equivalent
 */
function compare(left: RowSortValue, right: RowSortValue): number
{
    if (typeof left === 'number' && typeof right === 'number')
    {
        return left - right;
    }
    return String(left).localeCompare(String(right), undefined, {sensitivity: 'base', numeric: true});
}

function isBlank(value: RowSortValue): boolean
{
    return value === null || value === undefined || value === '';
}
